import { Device, GetPortalConfigResponse, User } from '../../../models/gen/graphql';
import { GetMapConfigDocument, GetPortalConfigDocument } from '../../queries';
import useSession, { SessionState } from '@/state/session';

import { GraphApiResponse } from '../../core';
import { PersistComponentStateType } from '@/state';
import createGraphApiHook from '../../../hooks/createGraphApiHook';
import { getBundleVersion } from '@/hooks/useVersionCheck';
import graphApi from '../..';

type GetPortalConfigGraphApiResponse = GraphApiResponse<typeof GetPortalConfigDocument>;
type GetMapConfigGraphApiResponse = GraphApiResponse<typeof GetMapConfigDocument>;

const device = Device.PortalV1;

const getPortalConfigResponseSelector = (res: GetPortalConfigGraphApiResponse | GetMapConfigGraphApiResponse): GetPortalConfigResponse =>
  res?.getPortalConfig as GetPortalConfigResponse;

const [, runRefetchGetPortalConfig] = graphApi(GetPortalConfigDocument);
const [, runRefetchGetMapConfig] = graphApi(GetMapConfigDocument);

const getPortalConfig = async (): Promise<GetPortalConfigResponse> => {
  const res = await runRefetchGetPortalConfig({ input: { device } });
  return getPortalConfigResponseSelector(res);
};

export const getMapConfig = async (): Promise<string> => {
  const res = await runRefetchGetMapConfig({ input: { device } });
  const data = getPortalConfigResponseSelector(res);
  return data?.googleMapsApiKey || '';
};

type GetConfigInput = { user?: User; incomingVersion?: string };
export const getConfig = async (input?: GetConfigInput) => {
  const portalConfigResponse = await getPortalConfig();
  const allowedURLS = portalConfigResponse?.userConfig?.menus?.navigation?.map((menu) => menu.url);
  useSession.setState((current: PersistComponentStateType<SessionState>): PersistComponentStateType<SessionState> => {
    const output = {
      ...(current || ({} as PersistComponentStateType<SessionState>)),
      state: {
        ...(current?.state || ({} as SessionState)),
        config: { ...portalConfigResponse },
        allowedUrls: allowedURLS,
        version: input?.incomingVersion || current?.state?.version || getBundleVersion()?.version,
      },
    };
    if (input?.user) output.state.user = input.user;
    return output;
  });
};
export const refreshConfig = async () => {
  const currentRelease = JSON.parse(sessionStorage.getItem('version')).release;
  const currentVersion = useSession?.getState()?.state?.version;
  if (!currentRelease) throw new Error('no release found.');
  if (!currentVersion) throw new Error('no current config version found.');
  if (currentVersion === currentRelease) return;
  await getConfig({
    incomingVersion: currentRelease,
  });
};

export const useGetPortalConfig = createGraphApiHook(getPortalConfig);

export default getPortalConfig;
